import { AnimatePresence } from "framer-motion";
import { Message, MessageType, OwnerTypeMessage } from "../../../models/Message";
import { DialogProps } from "../../Dialog";
import { DialogView, ModalBody, ModalHeader } from "./styles";
import { OutsideClick } from "../../OutSideClick";
import { DownloadSimple, X } from "@phosphor-icons/react";
import { IconButton } from "../../Buttons/IconButton";
import { Avatar } from "../../Avatar";
import { User } from "../../../models/User";
import { Contact } from "../../../models/Contact";
import { formatRelativeDateWithHour } from "../../../utils/formatRelativeDate";
import { MessageTypeWithoutText } from "../../../types/FileShow";
import { buildAttachmentFileURL, buildInlineFileURL } from "../../../utils/fileProxy";
import { useFileAccessURL } from "../../../hooks/useFileAccessURL";
import { useEffect, useRef, useState } from "react";
import { FileAccessMediaKind, getFileAccessReferences } from "../../../utils/fileAccessURL";
import { resolveMessageSenderAvatarReference, resolveMessageSenderName } from "../../../services/messageAttribution";
import { ImagePreviewContent } from "./ImagePreviewContent";

export interface FileShowModalProps extends Omit<DialogProps, "children">{
  message: Message;
  user: User;
  contact: Contact;
};

const uniqueNonEmpty = ( values: Array<string | null | undefined>) => {
  const unique = new Set<string>();

  values.forEach(( value ) => {
    const trimmed = ( value ?? "" ).trim();
    if( trimmed ) unique.add( trimmed );
  });

  return Array.from( unique );
};

export const FileShowModal = ({
  onClose = () => {},
  open,
  message,
  user,
  contact,
  ...rest
}: FileShowModalProps ) => {
  const dialogRef = useRef<HTMLDialogElement | null>( null );
  const isImage = message.messageType === MessageType.IMAGE;
  const mediaKind: FileAccessMediaKind = message.messageType === MessageType.IMAGE
    ? "image"
    : message.messageType === MessageType.VIDEO
      ? "video"
      : message.messageType === MessageType.AUDIO
        ? "audio"
        : "document";
  const inlineReferences = getFileAccessReferences(message.fileMetadata, "inline", mediaKind);
  const downloadReference = getFileAccessReferences(message.fileMetadata, "attachment", mediaKind)[0];
  const downloadURL = buildAttachmentFileURL( downloadReference );
  const inlineURL = useFileAccessURL({
    mode: "inline",
    fileMetadataId: message.fileMetadata?.id,
    reference: inlineReferences[0],
    enabled: open && !isImage,
  });
  const imageURLs = uniqueNonEmpty(inlineReferences.map((reference) => buildInlineFileURL(reference)));
  const mediaURLs = isImage ? imageURLs : uniqueNonEmpty([
    inlineURL,
    ...imageURLs,
  ]);
  const mediaURLsKey = mediaURLs.join("|");
  const [ mediaURLIndex, setMediaURLIndex ] = useState( 0 );
  const mediaURL = mediaURLs[ mediaURLIndex ] ?? "";

  useEffect(() => {
    setMediaURLIndex( 0 );
  }, [ mediaURLsKey ]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if( !open || !dialog ) return;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if( !dialog.open ) dialog.showModal();
    requestAnimationFrame(() => dialog.querySelector<HTMLButtonElement>( "button" )?.focus());

    return () => {
      if( dialog.open ) dialog.close();
      previouslyFocused?.focus();
    };
  }, [ open ]);

  const buildDownloadName = () => {
    const fileName = message.fileMetadata?.fileName ?? "arquivo";
    const extension = message.fileMetadata?.extension ?? "";

    if ( !extension ) {
      return fileName;
    }

    return fileName.toLowerCase().endsWith(`.${ extension.toLowerCase() }`)
      ? fileName
      : `${ fileName }.${ extension }`;
  };

  const renderFile = () => {
    const typeRenderMapper: {
      [ key: string ]: React.ReactNode;
    } = {
      image: (
        mediaURL ? (
          <ImagePreviewContent
            src={ mediaURL }
            alt={ message.fileMetadata?.fileName }
            onError={() => {
              setMediaURLIndex(( current ) => Math.min( current + 1, mediaURLs.length - 1 ));
            }}
          />
        ) : null
      ),
      audio: (
        <audio
          controls
          src={ inlineURL }
          autoPlay
        />
      ),
      video: (
        <video
          controls
          src={ inlineURL }
          autoPlay
          height="100%"
        />
      ),
      application: (
        <iframe
          src={`${ inlineURL }#toolbar=0`}
          title={ message.fileMetadata?.fileName ?? "preview-documento" }
          width="90%"
          height="100%"
        />
      ),
      text: (
        <></>
      ),
    };

    const mimeType = ( message.fileMetadata?.mimeType ?? "").split("/").shift() ?? "";

    const renderMapper: MessageTypeWithoutText = {
      [ MessageType.IMAGE ]: typeRenderMapper["image"],
      [ MessageType.VIDEO ]: typeRenderMapper["video"],
      [ MessageType.AUDIO ]: typeRenderMapper["audio"],
      [ MessageType.DOCUMENT ]: typeRenderMapper[ mimeType ],
    };

    return renderMapper[ message.messageType as keyof MessageTypeWithoutText ];
  };

  const senderName = resolveMessageSenderName( message, {
    currentUser: user,
    contactName: contact.name,
    emptyLabel: "Mensagem",
  });
  const senderAvatar = message.ownerType === OwnerTypeMessage.CONTACT
    ? contact.avatar
    : resolveMessageSenderAvatarReference( message );

  return (
    <AnimatePresence>
      {
        open && (
          <DialogView
            ref={ dialogRef }
            aria-label={ `Visualização de ${ message.fileMetadata?.fileName ?? "arquivo" }` }
            aria-modal="true"
            onCancel={( event ) => {
              event.preventDefault();
              onClose();
            }}
            initial={{
              opacity: 0,
              backdropFilter: "none",
            }}
            animate={{
              opacity: 1,
              backdropFilter: "blur( 2px )",
            }}
            exit={{
              opacity: 0,
              backdropFilter: "none",
            }}
            transition={{ duration: .2 }}
          >
            <ModalHeader onMouseDown={( event ) => event.stopPropagation()}>
              <div className="avatar-view">
                <Avatar
                  name={ senderName }
                  size={ 40 }
                  url={ buildInlineFileURL( senderAvatar )}
                />

                <div>
                  <span>{ senderName }</span>

                  <span>{ formatRelativeDateWithHour( message.createdAt )}</span>
                </div>
              </div>

              <div className="actions">
                <a
                  href={ downloadURL }
                  download={ buildDownloadName()}
                  className="download-button"
                  aria-label="Baixar arquivo"
                  title="Baixar arquivo"
                >
                  <DownloadSimple />
                </a>
              </div>

              <IconButton
                onClick={ onClose }
                aria-label="Fechar visualização do arquivo"
                title="Fechar"
              >
                <X />
              </IconButton>
            </ModalHeader>

            <OutsideClick
              executeCallback={ open }
              callback={ onClose }
            >
              <ModalBody>
                { renderFile()}
              </ModalBody>
            </OutsideClick>
          </DialogView>
        )
      }
    </AnimatePresence>
  );
};
